import {
  Component,
  DestroyRef,
  inject,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';

import { NomObjectClass } from '../../constants';
import { DataObject, User } from '../../models/core.models';
import { UtilsService } from '../../services/utils.service';

import { getNestedProperty } from 'src/app/shared/utils/object-utils';

import { Store } from '@ngrx/store';

import { AppState } from 'src/app/reducers';
import { selectUser } from '../../reducers';

import { tap } from 'rxjs';

interface StaticInfo {
  label: string;
  value: any;
  user?: boolean;
  copyToClipboard?: boolean;
  shouldDisplay?: boolean;
}

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrl: './audit-trail.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AuditTrailComponent {
  @Input() object: DataObject;
  @Input() hasOwner: boolean = false;
  @Input() hasCharts: boolean = false;
  @Input() timeCreatedKey: string;
  @Input() timeModifiedKey: string;

  user: User;
  staticInfo: StaticInfo[] = [];
  timeCreated: string;
  timeModified: string;

  protected utilService = inject(UtilsService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private translateService: TranslateService,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setTimeInfo();

    this.staticInfo = [
      {
        label: this.translateService.instant('audit-trail.label.object-id'),
        value: this.object.id,
        copyToClipboard: true,
      },
      {
        label: this.translateService.instant('audit-trail.label.owner'),
        value: this.object.owner,
        shouldDisplay: this.hasOwner,
        user: this.hasOwner,
      },
      {
        label: this.translateService.instant('audit-trail.label.time-created'),
        value: this.timeCreated,
      },
      {
        label: this.translateService.instant('audit-trail.label.time-modified'),
        value: this.timeModified,
      },
      {
        label: this.translateService.instant('audit-trail.label.chart-id'),
        value: this.hasCharts ? this.object['chart']?.id : null,
        shouldDisplay: this.hasCharts,
        copyToClipboard: true,
      },
      {
        label: this.translateService.instant('audit-trail.label.chart-type-id'),
        value: this.hasCharts ? this.object['chart_type']?.id : null,
        shouldDisplay: this.hasCharts,
        copyToClipboard: true,
      },
      {
        label: this.translateService.instant('audit-trail.label.chart-name'),
        value: this.hasCharts ? this.object['chart']?.name : null,
        shouldDisplay: this.hasCharts,
        copyToClipboard: true,
      },
      {
        label: this.translateService.instant('audit-trail.label.chart-type'),
        value: this.hasCharts ? this.object['chart_type']?.name : null,
        shouldDisplay: this.hasCharts,
        copyToClipboard: true,
      },
    ];
  }

  setTimeInfo() {
    this.timeCreated = getNestedProperty(
      this.object,
      this.timeCreatedKey,
      this.translateService.instant('audit-trail.label.not-available'),
    );
    this.timeModified = getNestedProperty(
      this.object,
      this.timeModifiedKey,
      this.translateService.instant('audit-trail.label.not-available'),
    );
  }

  copyToClipboard(value: string) {
    this.utilService.copyToClipboard(value);
  }

  protected readonly NomObjectClass = NomObjectClass;
}
